import { AuditResponse } from '@/api/data-contracts';
import { supportNames } from '@/shared/constants';

export const HISTORY_PAGE_SIZE = 20;

export const AVATAR_COLORS = ['#AC9AEB', '#7AB5E0', '#7AE0C4', '#E0B07A', '#E07A7A', '#B0E07A'];

export const VISUAL_RESOURCE_TYPES = new Set([
  'MEDIA_PLAN_KEY_VISUALS_ADDED',
  'MEDIA_PLAN_KEY_VISUALS_DELETED',
]);

export const BOOKING_PERIOD_TYPES = new Set([
  'ORDER_BOOKING_CREATED',
  'ORDER_BOOKING_REQUESTED',
  'ORDER_BOOKING_APPROVED',
  'ORDER_BOOKING_REJECTED',
]);

export const BOOKING_NAME_TYPES = new Set(['ORDER_PARTIALLY_APPROVED', 'ORDER_PARTIALLY_REJECTED']);

export type JsonPatchOp = Record<string, unknown> & { op?: string; path?: string };

export type DiffDisplayOp = {
  op: JsonPatchOp;
  kind:
    | 'status'
    | 'text'
    | 'number'
    | 'date'
    | 'iso-date'
    | 'currency'
    | 'service-list'
    | 'booking-list';
};

export type HistoryAttachment = {
  id: string;
  name: string;
  filePointer: { objectId: string; name: string };
};

export type ArtworkEntry = {
  orderId: string;
  bookingId: string;
  objectId: string;
  attachment: HistoryAttachment;
};

export type MediaPlanArtworkEntry = {
  planId: string;
  bookingId: string;
  objectId: string;
  isPreview: boolean;
  attachment: HistoryAttachment;
};

type VisualAttachmentFact = { attachmentId: string; name: string; objectId: string };

export function parseExtraDetails(raw: string): JsonPatchOp[] {
  if (!raw) return [];
  try {
    let str = raw;
    if (str.startsWith('"') && str.endsWith('"')) {
      str = str.slice(1, -1);
      str = str.replace(/\\(.)/g, '$1');
    }
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function extractArtworkFiles(
  extraDetails: string,
): Array<{ file: VisualAttachmentFact; isPreview: boolean }> {
  return parseExtraDetails(extraDetails)
    .filter((op) => op.op === 'add' && Array.isArray(op.value))
    .flatMap((op) =>
      (op.value as VisualAttachmentFact[]).map((file) => ({
        file,
        isPreview: typeof op.path === 'string' && op.path.includes('artWorkPreview'),
      })),
    )
    .filter(({ file }) => !!file?.objectId);
}

function toHistoryAttachment(file: VisualAttachmentFact): HistoryAttachment {
  return {
    id: file.attachmentId,
    name: file.name,
    filePointer: { objectId: file.objectId, name: file.name },
  };
}

export function parseResponsibleUsers(
  details?: string | null,
): Array<{ userName: string; userId: string }> {
  if (!details) return [];
  try {
    const d = JSON.parse(details);
    const responsibleUsers = d.responsibleUsers;
    if (!responsibleUsers || typeof responsibleUsers !== 'object') return [];
    return (Object.values(responsibleUsers) as any[])
      .map((u) => ({ userName: u.userName ?? '', userId: u.userId ?? '' }))
      .filter((u) => u.userName);
  } catch {
    return [];
  }
}

export function isArtworkPreviewUpdate(extraDetails?: string | null): boolean {
  const ops = extraDetails ? parseExtraDetails(extraDetails) : [];
  return ops.some(
    (op) => op?.op === 'add' && typeof op.path === 'string' && op.path.includes('artWorkPreview'),
  );
}

export function getArtworkUpdateActionText(extraDetails?: string | null): string {
  return isArtworkPreviewUpdate(extraDetails) ? 'uploaded artwork preview' : 'uploaded artwork';
}

export function isPurchasedServicesUpdate(extraDetails?: string | null): boolean {
  const ops = extraDetails ? parseExtraDetails(extraDetails) : [];
  return ops.some((op) => diffSegment(op) === 'purchasedServices');
}

/**
 * The essential facts about one booked space, as the order/media-plan page already has them.
 * `id` is the BOOKING id (not the space id) — that is what the audit rows name their subject by.
 */
export interface SpaceBasicInfo {
  id: string;
  /** The SPACE id — some rows name the space rather than the booking. */
  spaceId?: string;
  support?: string;
  locationName: string;
  exposureType?: string;
  floor?: string;
  placementArea?: string;
  placementAreaName?: string;
  /** Set when this space was booked as part of a package. */
  packageName?: string;
}

export interface PackageBasicInfo {
  id: string;
  packageName: string;
  locationName: string;
}

/**
 * The two booking families the dialog is given: an ORDER booking nests its snapshot under
 * `space.spaceResponse` / `advertisingPackage.advertisingPackageResponse`, a MEDIA-PLAN booking
 * carries it inline. Reading both here keeps the two pages from each writing their own mapping.
 */
type SubjectSpaceSnapshot = {
  support?: string;
  exposureType?: string;
  floor?: string;
  placementArea?: string;
  placementAreaName?: string;
  location?: { commercialName?: string };
};

type SubjectSpaceSource = {
  id?: string;
  space?:
    | ({
        spaceId?: string;
        id?: string;
        spaceResponse?: SubjectSpaceSnapshot;
      } & SubjectSpaceSnapshot)
    | null;
};

type SubjectPackageSource = {
  id?: string;
  advertisingPackage?:
    | ({ advertisingPackageResponse?: { packageName?: string } } & { packageName?: string })
    | null;
  bookings?: SubjectSpaceSource[];
};

const spaceSnapshot = (booking: SubjectSpaceSource): SubjectSpaceSnapshot =>
  booking.space?.spaceResponse ?? booking.space ?? {};

const toSpaceBasicInfo = (booking: SubjectSpaceSource, packageName?: string): SpaceBasicInfo => {
  const space = spaceSnapshot(booking);
  return {
    id: booking.id ?? '',
    spaceId: booking.space?.spaceId ?? booking.space?.id,
    support: space.support,
    exposureType: space.exposureType,
    floor: space.floor,
    placementArea: space.placementArea,
    placementAreaName: space.placementAreaName,
    locationName: space.location?.commercialName ?? '',
    packageName,
  };
};

/** The `spaces` / `packages` the dialog wants, from whatever booking collections a page holds. */
export function toHistorySubjectSources(
  bookings?: SubjectSpaceSource[],
  packageBookings?: SubjectPackageSource[],
): { spaces: SpaceBasicInfo[]; packages: PackageBasicInfo[] } {
  const spaces = (bookings ?? []).map((booking) => toSpaceBasicInfo(booking));
  const packages = (packageBookings ?? []).map((pkg) => {
    const snapshot = pkg.advertisingPackage?.advertisingPackageResponse ?? pkg.advertisingPackage;
    const packageName = snapshot?.packageName ?? '';
    (pkg.bookings ?? []).forEach((booking) => spaces.push(toSpaceBasicInfo(booking, packageName)));
    return {
      id: pkg.id ?? '',
      packageName,
      locationName: spaceSnapshot(pkg.bookings?.[0] ?? {}).location?.commercialName ?? '',
    };
  });
  return { spaces, packages };
}

/** What a history row happened TO: the space or package behind the event's booking id. */
export type HistorySubject = {
  kind: 'space' | 'package';
  /** Support name for a space ("Billboard"), package name for a package. */
  name: string;
  placementAreaName?: string;
  locationName?: string;
  /** Set on a space that was booked as part of a package. */
  packageName?: string;
};

export type HistorySubjectIndex = Map<string, HistorySubject>;

/**
 * Booking id → what it is. Audit rows name their subject by id only, so the names have to come
 * from the spaces/packages the page already loaded.
 */
export function buildHistorySubjectIndex(
  spaces: SpaceBasicInfo[],
  packages: PackageBasicInfo[],
): HistorySubjectIndex {
  const index: HistorySubjectIndex = new Map();
  spaces.forEach((space) => {
    if (!space.id && !space.spaceId) return;
    const subject: HistorySubject = {
      kind: 'space',
      name: supportNames.find((s) => s.value === space.support)?.label ?? space.support ?? '',
      placementAreaName: space.placementAreaName,
      locationName: space.locationName,
      packageName: space.packageName,
    };
    if (space.id) index.set(space.id, subject);
    if (space.spaceId) index.set(space.spaceId, subject);
  });
  packages.forEach((pkg) => {
    if (!pkg.id) return;
    index.set(pkg.id, {
      kind: 'package',
      name: pkg.packageName ?? '',
      locationName: pkg.locationName,
    });
  });
  return index;
}

export function getHistorySubject(
  item: AuditResponse,
  index: HistorySubjectIndex,
): HistorySubject | null {
  let spaceName: string | undefined;
  let locationName: string | undefined;
  try {
    const d = JSON.parse(item.details ?? '{}');
    spaceName = typeof d.spaceName === 'string' && d.spaceName ? d.spaceName : undefined;
    locationName =
      typeof d.locationName === 'string' && d.locationName ? d.locationName : undefined;
  } catch (_e) {
    /* ignore */
  }

  // The scheduler-driven events denormalise the names onto the row itself — no lookup needed, and
  // they stay right even for a booking that has since left the order.
  if (spaceName) return { kind: 'space', name: spaceName, locationName };

  const subjectId = getHistoryBookingId(item);
  const indexed = subjectId ? index.get(subjectId) : undefined;
  if (indexed) return indexed;

  // The created/deleted folds name no booking id of their own — they list the bookings instead.
  return subjectFromListedBookings(item.details, index);
}

/**
 * The subject for a row that lists its bookings in `details.bookings[]`. Each entry is resolved
 * through the index first (which yields the support / placement / location), and falls back to
 * whatever name the entry itself carries. Several bookings collapse to their names alone.
 */
function subjectFromListedBookings(
  details: string | null | undefined,
  index: HistorySubjectIndex,
): HistorySubject | null {
  let listed: Record<string, unknown>[] = [];
  try {
    const parsed = JSON.parse(details ?? '{}');
    listed = Array.isArray(parsed?.bookings) ? parsed.bookings : [];
  } catch (_e) {
    return null;
  }

  const resolved = listed
    .map((entry) => {
      const byId = [entry?.id, entry?.bookingId, entry?.spaceId]
        .map((id) => (typeof id === 'string' && id ? index.get(id) : undefined))
        .find(Boolean);
      if (byId) return byId;
      const supportName = entry?.supportName;
      const placementAreaName = entry?.placementAreaName;
      const location = entry?.location;
      const name = entry?.name;
      if (typeof supportName === 'string' && supportName) {
        return {
          kind: 'space' as const,
          name: supportName,
          placementAreaName: typeof placementAreaName === 'string' ? placementAreaName : undefined,
          locationName: typeof location === 'string' ? location : undefined,
        };
      }
      if (typeof name === 'string' && name) {
        return {
          kind: 'space' as const,
          name,
          placementAreaName: typeof placementAreaName === 'string' ? placementAreaName : undefined,
          locationName: typeof location === 'string' ? location : undefined,
        };
      }
      return null;
    })
    .filter((subject): subject is HistorySubject => !!subject);

  if (!resolved.length) return null;
  if (resolved.length === 1) return resolved[0];
  return {
    kind: 'space',
    name: resolved
      .map((s) => [s.name, s.placementAreaName, s.locationName].filter(Boolean).join(' \u00b7 '))
      .filter(Boolean)
      .join(', '),
  };
}

/** Everything after the name: where the space sits, and the package it was booked in. */
export function historySubjectDetail(subject: HistorySubject): string {
  return [subject.placementAreaName, subject.locationName, subject.packageName]
    .filter(Boolean)
    .join(' \u00b7 ');
}

export function formatHistorySubject(subject: HistorySubject): string {
  return [subject.name, historySubjectDetail(subject)].filter(Boolean).join(' \u00b7 ');
}

function pluralizeBookingAction(
  details: string | null | undefined,
  singular: string,
  plural: string,
): string {
  return parseListedBookings(details).length > 1 ? plural : singular;
}

export function getOrderActionText(
  type: string,
  extraDetails?: string | null,
  details?: string | null,
): string {
  switch (type) {
    case 'MEDIA_PLAN_UPDATED':
      return bulkBookingAddActionText(extraDetails);
    case 'ORDER_BOOKING_ARTWORK_UPDATED':
    case 'MEDIA_PLAN_BOOKING_ARTWORK_UPDATED':
      return getArtworkUpdateActionText(extraDetails);
    case 'ORDER_BOOKING_UPDATED':
    case 'MEDIA_PLAN_BOOKING_UPDATED':
      return isPurchasedServicesUpdate(extraDetails) ? 'updated services' : '';
    case 'ORDER_LOCATION_RESPONSIBLE_ASSIGNED':
      return 'assigned responsible';
    case 'ORDER_CONFIRMED':
      return 'confirmed order';
    case 'ORDER_CANCELLED':
      return 'cancelled order';
    case 'ORDER_APPROVED':
      return 'approved order';
    case 'ORDER_REJECTED':
      return 'rejected order';
    case 'ORDER_PARTIALLY_APPROVED':
      return 'partially approved order';
    case 'ORDER_PARTIALLY_REJECTED':
      return 'partially rejected order';
    case 'ORDER_BOOKING_CREATED':
      return pluralizeBookingAction(details, 'added a new booking', 'added new bookings');
    case 'ORDER_BOOKING_EXTENSION_SUBMITTED':
      return 'requested extension';
    case 'ORDER_BOOKING_EXTENSION_APPROVED':
      return 'approved extension';
    case 'ORDER_BOOKING_APPROVED':
      return 'approved booking';
    case 'ORDER_BOOKING_REJECTED':
      return 'rejected booking';
    case 'ORDER_BOOKING_DELETED':
      return pluralizeBookingAction(details, 'deleted booking', 'deleted bookings');
    case 'MEDIA_PLAN_BOOKING_DELETED':
      return pluralizeBookingAction(details, 'removed a booking', 'removed bookings');
    case 'ORDER_BOOKING_REQUESTED':
      return pluralizeBookingAction(details, 'requested booking', 'requested bookings');
    case 'ORDER_BOOKING_CAMPAIGN_PHOTOS_UPDATED':
      return 'uploaded campaign photos';
    case 'ORDER_KEY_VISUALS_UPLOADED':
      return 'uploaded visual resources';
    case 'ORDER_KEY_VISUALS_DELETED':
      return 'deleted visual resources';
    case 'ORDER_DISCOUNT_APPROVED':
      return 'approved discount';
    case 'ORDER_DISCOUNT_REJECTED':
      return 'rejected discount';
    default:
      return '';
  }
}

const ALWAYS_SKIP_DIFF_SEGMENTS = new Set([
  'id',
  'createdAt',
  'createdBy',
  'updatedAt',
  'updatedBy',
  'operationStatus',
  'filters',
]);

const EXTENSION_DATE_SEGMENT = 'timeframeEnd';

/** Mirrors the backend's own lowercase-contains guard so a newly added selling field is covered
 *  without touching this list. The server already withholds these rows; this is the second lock. */
const SELLING_DIFF_MARKERS = ['selling', 'commercialaddition'];

const isSellingDiffPath = (op: JsonPatchOp): boolean => {
  const path = op.path?.toLowerCase() ?? '';
  return SELLING_DIFF_MARKERS.some((marker) => path.includes(marker));
};

const DATE_DIFF_SEGMENTS = new Set(['start', 'end']);

const CURRENCY_DIFF_SEGMENTS = new Set(['price', 'budget', 'priceContribution', 'monthlyPrice']);

function diffSegment(op: JsonPatchOp): string {
  return op.path?.split('/').filter(Boolean).pop() ?? '';
}

function looksNumeric(value: unknown): boolean {
  if (typeof value === 'number') return true;
  return typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value));
}

function inferDiffKind(segment: string, value: unknown): DiffDisplayOp['kind'] {
  if (segment === 'status') return 'status';
  if (segment === 'purchasedServices') return 'service-list';
  if (segment === 'bookings' && Array.isArray(value)) return 'booking-list';
  if (DATE_DIFF_SEGMENTS.has(segment)) return 'date';
  if (CURRENCY_DIFF_SEGMENTS.has(segment) && looksNumeric(value)) return 'currency';
  if (looksNumeric(value)) return 'number';
  return 'text';
}

export function humanizeServiceList(raw: string): string {
  if (!raw) return 'None';
  return raw
    .split(',')
    .filter(Boolean)
    .map((token) =>
      token
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
    )
    .join(', ');
}

export function humanizeFieldLabel(segment: string): string {
  const spaced = segment
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function getDiffFieldLabel(op: JsonPatchOp): string {
  return humanizeFieldLabel(diffSegment(op));
}

function isPackageBookingFact(entry: BookingFactLike): boolean {
  return !entry?.supportName && !entry?.placementAreaName;
}

function bulkAddedBookingCounts(
  extraDetails?: string | null,
): { packages: number; spaces: number } | null {
  const ops = extraDetails ? parseExtraDetails(extraDetails) : [];
  const addOp = ops.find(
    (op) => op.op === 'add' && diffSegment(op) === 'bookings' && Array.isArray(op.value),
  );
  if (!addOp) return null;
  const items = addOp.value as BookingFactLike[];
  return items.reduce(
    (counts, entry) => {
      if (isPackageBookingFact(entry)) return { ...counts, packages: counts.packages + 1 };
      return { ...counts, spaces: counts.spaces + 1 };
    },
    { packages: 0, spaces: 0 },
  );
}

function bulkBookingAddActionText(extraDetails?: string | null): string {
  const counts = bulkAddedBookingCounts(extraDetails);
  if (!counts) return '';
  const parts: string[] = [];
  if (counts.packages > 0) parts.push(counts.packages > 1 ? 'new packages' : 'new package');
  if (counts.spaces > 0) parts.push(counts.spaces > 1 ? 'new spaces' : 'new space');
  return parts.length > 0 ? `added ${parts.join(', ')}` : 'added new bookings';
}

export function getOrderOpsForDisplay(
  extraDetails: string,
  canViewSellingPrices = false,
): DiffDisplayOp[] {
  const ops = parseExtraDetails(extraDetails).filter((op) => !!op.op);
  return ops
    .filter((op) => {
      const seg = diffSegment(op);
      if (op.op === 'add' && seg === 'bookings' && Array.isArray(op.value)) return true;
      return (
        op.op === 'replace' &&
        op.oldValue != null &&
        !ALWAYS_SKIP_DIFF_SEGMENTS.has(seg) &&
        seg !== EXTENSION_DATE_SEGMENT &&
        (canViewSellingPrices || !isSellingDiffPath(op))
      );
    })
    .map((op) => ({ op, kind: inferDiffKind(diffSegment(op), op.value) }));
}

export function getExtensionDateOp(extraDetails?: string | null): DiffDisplayOp | null {
  if (!extraDetails) return null;
  const op = parseExtraDetails(extraDetails).find(
    (o) => o.op === 'replace' && diffSegment(o) === EXTENSION_DATE_SEGMENT && o.oldValue != null,
  );
  return op ? { op, kind: 'iso-date' } : null;
}

export function formatDateShort(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export type CountdownInfo = { days: number; isOverdue: boolean; date: string | null };

export function formatIsoDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function getCountdownInfo(details?: string | null): CountdownInfo | null {
  if (!details) return null;
  try {
    const d = JSON.parse(details);
    const date = typeof d.bookingStart === 'string' ? formatIsoDateShort(d.bookingStart) : null;
    if (typeof d.overdueDays === 'number') return { days: d.overdueDays, isOverdue: true, date };
    if (typeof d.remainingDays === 'number')
      return { days: d.remainingDays, isOverdue: false, date };
    if (typeof d.daysLeft === 'number') return { days: d.daysLeft, isOverdue: false, date };
  } catch (_e) {
    /* ignore */
  }
  return null;
}

export function getHistoryBookingId(item: AuditResponse): string | null {
  try {
    const d = JSON.parse(item.details ?? '{}');
    if (typeof d.bookingId === 'string' && d.bookingId) return d.bookingId;
    // A package booking's own row keys the id as packageBookingId; the approve/reject folds
    // key it as resourceId.
    if (typeof d.packageBookingId === 'string' && d.packageBookingId) return d.packageBookingId;
    if (typeof d.resourceId === 'string' && d.resourceId) return d.resourceId;
  } catch (_e) {
    /* ignore */
  }
  if (item.extraDetails) {
    const fromExtra = parseExtraDetails(item.extraDetails).find(
      (op) => typeof op?.bookingId === 'string' && op.bookingId,
    );
    if (fromExtra) return fromExtra.bookingId as string;
  }
  return null;
}

export function getVisualResourceCount(
  details?: string | null,
): { prev: number; next: number } | null {
  if (!details) return null;
  try {
    const d = JSON.parse(details);
    if (typeof d.previousCount === 'number' && typeof d.newCount === 'number') {
      return { prev: d.previousCount, next: d.newCount };
    }
  } catch (_e) {
    /* ignore */
  }
  return null;
}

function formatAvailabilityTimestamp(value: unknown): string | null {
  if (typeof value === 'number') return formatDateShort(value);
  if (typeof value === 'string' && value) return formatIsoDateShort(value);
  return null;
}

function formatAvailabilityPeriod(availability): string | null {
  const start = formatAvailabilityTimestamp(availability?.start);
  const end = formatAvailabilityTimestamp(availability?.end);
  return start && end ? `${start} – ${end}` : null;
}

type BookingFactLike = {
  name?: string | null;
  availability?: unknown;
  supportName?: string | null;
  placementAreaName?: string | null;
  location?: string | null;
  locationName?: string | null;
};

function parseListedBookings(details?: string | null): BookingFactLike[] {
  if (!details) return [];
  try {
    const d = JSON.parse(details);
    return Array.isArray(d.bookings) ? d.bookings : [];
  } catch {
    return [];
  }
}

function hasMixedBookingKinds(bookings: BookingFactLike[]): boolean {
  const hasPackage = bookings.some(isPackageBookingFact);
  const hasSpace = bookings.some((b) => !isPackageBookingFact(b));
  return hasPackage && hasSpace;
}

function formatBookingFactLabel(
  booking: BookingFactLike,
  showPackagePrefix: boolean,
): string | null {
  const supportName =
    typeof booking?.supportName === 'string' && booking.supportName ? booking.supportName : null;
  const placementAreaName =
    typeof booking?.placementAreaName === 'string' && booking.placementAreaName
      ? booking.placementAreaName
      : null;
  const name = typeof booking?.name === 'string' && booking.name ? booking.name : null;
  const locationName =
    (typeof booking?.locationName === 'string' && booking.locationName) ||
    (typeof booking?.location === 'string' && booking.location) ||
    null;

  if (isPackageBookingFact(booking)) {
    const parts = [name, locationName].filter((v): v is string => !!v);
    if (parts.length === 0) return null;
    const label = parts.join(' \u00b7 ');
    return showPackagePrefix ? `Package: ${label}` : label;
  }
  const parts = [supportName, placementAreaName ?? name, locationName].filter(
    (v): v is string => typeof v === 'string' && v.length > 0,
  );
  return parts.length > 0 ? parts.join(' \u00b7 ') : null;
}

export function getBookingPeriod(details?: string | null): string | null {
  const periods = parseListedBookings(details)
    .map((booking) => formatAvailabilityPeriod(booking?.availability))
    .filter((p): p is string => !!p);
  return periods.length > 0 ? [...new Set(periods)].join(', ') : null;
}

export function getBookingName(details?: string | null): string | null {
  const names = parseListedBookings(details)
    .map((booking) => (typeof booking?.name === 'string' && booking.name ? booking.name : null))
    .filter((n): n is string => !!n);
  return names.length > 0 ? names.join(', ') : null;
}

export function getBookingsSummary(details?: string | null): string | null {
  const bookings = parseListedBookings(details);
  const showPackagePrefix = hasMixedBookingKinds(bookings);
  const labels = bookings
    .map((booking) => formatBookingFactLabel(booking, showPackagePrefix))
    .filter((l): l is string => !!l);
  return labels.length > 0 ? labels.join(', ') : null;
}

export function formatBookingListValue(value: unknown): string {
  const bookings = Array.isArray(value) ? (value as BookingFactLike[]) : [];
  const showPackagePrefix = hasMixedBookingKinds(bookings);
  return bookings
    .map((booking) => formatBookingFactLabel(booking, showPackagePrefix))
    .filter((l): l is string => !!l)
    .join(', ');
}

const BULK_BOOKING_MERGE_WINDOW_MS = 5000;

function detailsBookingsList(details?: string | null): BookingFactLike[] | null {
  if (!details) return null;
  try {
    const parsed = JSON.parse(details);
    return Array.isArray(parsed?.bookings) ? parsed.bookings : null;
  } catch {
    return null;
  }
}

function withMergedDetailsBookings(
  details: string | null | undefined,
  extra: BookingFactLike[],
): string {
  const base = details ? JSON.parse(details) : {};
  return JSON.stringify({ ...base, bookings: [...(base.bookings ?? []), ...extra] });
}

function extraDetailsAddedBookings(extraDetails?: string | null): BookingFactLike[] | null {
  if (!extraDetails) return null;
  const op = parseExtraDetails(extraDetails).find(
    (o) => o.op === 'add' && diffSegment(o) === 'bookings' && Array.isArray(o.value),
  );
  return op ? (op.value as BookingFactLike[]) : null;
}

function withMergedExtraDetailsAddedBookings(
  extraDetails: string | null | undefined,
  extra: BookingFactLike[],
): string {
  const ops = extraDetails ? parseExtraDetails(extraDetails) : [];
  const merged = ops.map((op) =>
    op.op === 'add' && diffSegment(op) === 'bookings' && Array.isArray(op.value)
      ? { ...op, value: [...(op.value as BookingFactLike[]), ...extra] }
      : op,
  );
  return JSON.stringify(merged);
}

export function groupBulkBookingActionRows(items: AuditResponse[]): AuditResponse[] {
  const result: AuditResponse[] = [];
  let previousItem: AuditResponse | null = null;
  for (const item of items) {
    const prev = result[result.length - 1];
    const closeEnough =
      !!prev &&
      !!previousItem &&
      previousItem.type === item.type &&
      previousItem.userId === item.userId &&
      Math.abs(new Date(previousItem.timestamp).getTime() - new Date(item.timestamp).getTime()) <=
        BULK_BOOKING_MERGE_WINDOW_MS;
    previousItem = item;

    if (closeEnough && prev) {
      const prevDetailsBookings = detailsBookingsList(prev.details);
      const itemDetailsBookings = detailsBookingsList(item.details);
      if (prevDetailsBookings && itemDetailsBookings) {
        result[result.length - 1] = {
          ...prev,
          details: withMergedDetailsBookings(prev.details, itemDetailsBookings),
        };
        continue;
      }

      const prevAdded = extraDetailsAddedBookings(prev.extraDetails);
      const itemAdded = extraDetailsAddedBookings(item.extraDetails);
      if (prevAdded && itemAdded) {
        result[result.length - 1] = {
          ...prev,
          extraDetails: withMergedExtraDetailsAddedBookings(prev.extraDetails, itemAdded),
        };
        continue;
      }
    }
    result.push(item);
  }
  return result;
}

export function getArtworkEntries(item: AuditResponse): ArtworkEntry[] {
  if (item.type !== 'ORDER_BOOKING_ARTWORK_UPDATED') return [];
  let orderId = '';
  let bookingId = '';
  try {
    const d = JSON.parse(item.details ?? '{}');
    orderId = d.orderId ?? '';
    bookingId = d.bookingId ?? '';
  } catch (_e) {
    /* ignore */
  }
  if (!orderId || !bookingId) return [];
  return extractArtworkFiles(item.extraDetails ?? '').map(({ file }) => ({
    orderId,
    bookingId,
    objectId: file.objectId,
    attachment: toHistoryAttachment(file),
  }));
}

export type HistoryArtworkFocus = { attachmentId: string; isPreview: boolean };

export function getHistoryArtworkFocus(item: AuditResponse): HistoryArtworkFocus | null {
  const attachmentId = getArtworkEntries(item)[0]?.attachment?.id;
  if (!attachmentId) return null;
  return { attachmentId, isPreview: isArtworkPreviewUpdate(item.extraDetails) };
}

export function getMediaPlanArtworkEntries(item: AuditResponse): MediaPlanArtworkEntry[] {
  if (item.type !== 'MEDIA_PLAN_BOOKING_ARTWORK_UPDATED') return [];
  let planId = '';
  let bookingId = '';
  try {
    const d = JSON.parse(item.details ?? '{}');
    planId = d.planId ?? '';
    bookingId = d.bookingId ?? '';
  } catch (_e) {
    /* ignore */
  }
  if (!planId || !bookingId) return [];
  return extractArtworkFiles(item.extraDetails ?? '').map(({ file, isPreview }) => ({
    planId,
    bookingId,
    objectId: file.objectId,
    isPreview,
    attachment: toHistoryAttachment(file),
  }));
}

export function getAvatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function abbreviateLastName(name: string): string {
  const parts = name.trim().split(' ');
  if (parts.length < 2) return name;
  return parts[parts.length - 1] + ' ' + parts[0][0] + '.';
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) +
    ' at ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  );
}
